<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Order Number Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.order_number')"
                v-model.trim="filterOptions.order_number"
              />
              <!-- End:: Order Number Input -->

              <!-- Start:: Order Type Input -->
              <base-select-input
                col="4"
                :optionsList="orderTypes"
                :placeholder="$t('PLACEHOLDERS.order_type')"
                v-model="filterOptions.order_type"
              />
              <!-- End:: Order Type Input -->

              <!-- Start:: Client Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.client_name')"
                v-model.trim="filterOptions.user_name"
              />
              <!-- End:: Client Name Input -->

              <!-- Start:: Provider Name Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.provider_name')"
                v-model.trim="filterOptions.provider_name"
              />
              <!-- End:: Provider Name Input -->

              <!-- Start:: Comment Status Input -->
              <base-select-input
                col="6"
                :optionsList="commentStatuses"
                :placeholder="$t('COMMENTS.comment_status')"
                v-model="filterOptions.is_active"
              />
              <!-- End:: Comment Status Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("COMMENTS.comments_management") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:[`item.id`]="{ index }">
          {{ (paginations.current_page - 1) * paginations.items_per_page + index + 1 }}
        </template>
        
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Order Number -->
        <template v-slot:[`item.order_id`]="{ item }">
          <button 
            class="text-primary text-decoration-underline border-0 bg-transparent"
            @click="navigateToOrder(item)"
          >
            {{ item.order_id }}
          </button>
        </template>
        <!-- End:: Order Number -->

        <!-- Start:: Comment Text -->
        <template v-slot:[`item.comment`]="{ item }">
          <button 
            class="text-primary text-decoration-underline border-0 bg-transparent"
            @click="showCommentModal(item.comment)"
          >
            {{ item.comment ? item.comment.substring(0, 50) + (item.comment.length > 50 ? '...' : '') : '' }}
          </button>
        </template>
        <!-- End:: Comment Text -->

        <!-- Start:: Order Type -->
        <template v-slot:[`item.order_type_label`]="{ item }">
          {{ item.order_type_label }}
        </template>
        <!-- End:: Order Type -->
         
        <!-- Start:: Activation Switch -->
        <template v-slot:[`item.is_active`]="{ item }">
          <div
            class="activation"
            dir="ltr"
            style="z-index: 1"
            v-if="$can('reviews activate', 'reviews')"
          >
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="changeActivationStatus(item)"
            ></v-switch>
          </div>
        </template>
        <!-- End:: Activation Switch -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Comment Modal -->
          <description-modal
            v-if="dialogComment"
            :modalIsOpen="dialogComment"
            :modalDesc="selectedCommentToShow"
            @toggleModal="dialogComment = !dialogComment"
          />
          <!-- End:: Comment Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllComments",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    orderTypes() {
      return [
        {
          id: 1,
          name: this.$t("ORDER_TYPES.horse_purchase"),
          value: "horse_purchase",
        },
        {
          id: 2,
          name: this.$t("ORDER_TYPES.horse_rent"),
          value: "horse_rent",
        },
        {
          id: 3,
          name: this.$t("ORDER_TYPES.room_rent"),
          value: "room_rent",
        },
        {
          id: 4,
          name: this.$t("ORDER_TYPES.carrier_service"),
          value: "carrier_service",
        },
        {
          id: 5,
          name: this.$t("ORDER_TYPES.products"),
          value: "products",
        },
        {
          id: 6,
          name: this.$t("ORDER_TYPES.other_service"),
          value: "other_service",
        },
      ];
    },

    commentStatuses() {
      return [
        {
          id: 1,
          name: this.$t("COMMENTS.published"),
          value: 1,
        },
        {
          id: 2,
          name: this.$t("COMMENTS.hidden"),
          value: 0,
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        order_number: null,
        order_type: null,
        user_name: null,
        provider_name: null,
        is_active: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.order_number"),
          value: "order_id",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.client_name"),
          value: "user_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.provider_name"),
          value: "provider_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.order_type"),
          value: "order_type_label",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("COMMENTS.comment_text"),
          value: "comment",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("COMMENTS.comment_status"),
          value: "is_active",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogComment: false,
      selectedCommentToShow: "",
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/comments/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.order_number = null;
      this.filterOptions.order_type = null;
      this.filterOptions.user_name = null;
      this.filterOptions.provider_name = null;
      this.filterOptions.is_active = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/comments/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Comments
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "reviews",
          params: {
            page: this.paginations.current_page,
            order_id: this.filterOptions.order_number,
            order_type: this.filterOptions.order_type?.value,
            user_name: this.filterOptions.user_name,
            provider_name: this.filterOptions.provider_name,
            is_active: this.filterOptions.is_active?.value,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.last_page || 1;
        this.paginations.items_per_page = res.data.data.per_page || 15;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Show Comment Modal
    showCommentModal(comment) {
      this.dialogComment = true;
      this.selectedCommentToShow = comment;
    },
    // End:: Show Comment Modal

    // Start:: Navigate To Order
    navigateToOrder(item) {
      // Map order types to their routes
      const orderTypeRoutes = {
        'horse_purchase': '/horse-purchase/show/',
        'horse_rent': '/horse-rent/show/',
        'room_rent': '/room-rent/show/',
        'carrier_service': '/carrier-service/show/',
        'products': '/product-orders/show/',
        'other_service': '/other-service/show/',
      };
      
      const route = orderTypeRoutes[item.order_type];
      if (route) {
        this.$router.push({ path: `${route}${item.order_id}` });
      }
    },
    // End:: Navigate To Order

    // Start:: Change Activation Status (Publish/Hide)
    async changeActivationStatus(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `reviews/${item.id}/toggle-status`,
        });
        this.setTableRows();
        this.$message.success(this.$t("PLACEHOLDERS.changeCommentStatusSuccess"));
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Change Activation Status
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>
